/**
 * VistoeraTASK-00625 — one-shot seed: Image Assets "加入画布" → new blank
 * Content Project Canvas opens with a single image_asset node.
 *
 * Stored in sessionStorage (not React state / not URL) so:
 * - the Workspace→Canvas Enter animation can use the existing
 *   `openCanvasFromWorkspace` intent flag without mixing business data;
 * - a hard refresh of the Canvas route cannot re-seed (consume clears);
 * - an old project is never mutated — seed is keyed to the NEW projectId.
 */

export const JOIN_IMAGE_ASSET_SEED_KEY = 'vistoera.canvas.joinImageAssetSeed.v1'

export type JoinImageAssetSeed = {
  workspaceId: string
  projectId: string
  siteAssetId: string
  pickerItemId?: string
  filename?: string | null
  nonce: string
}

function readRaw(): JoinImageAssetSeed | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(JOIN_IMAGE_ASSET_SEED_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<JoinImageAssetSeed>
    if (
      !parsed ||
      typeof parsed.workspaceId !== 'string' ||
      typeof parsed.projectId !== 'string' ||
      typeof parsed.siteAssetId !== 'string' ||
      typeof parsed.nonce !== 'string'
    ) {
      return null
    }
    return {
      workspaceId: parsed.workspaceId,
      projectId: parsed.projectId,
      siteAssetId: parsed.siteAssetId,
      pickerItemId: typeof parsed.pickerItemId === 'string' ? parsed.pickerItemId : undefined,
      filename: parsed.filename ?? null,
      nonce: parsed.nonce,
    }
  } catch {
    return null
  }
}

export function writeJoinImageAssetSeed(seed: JoinImageAssetSeed): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(JOIN_IMAGE_ASSET_SEED_KEY, JSON.stringify(seed))
  } catch {
    /* ignore quota / private mode */
  }
}

/** Returns the seed only when it matches this workspace+project, then clears it. */
export function consumeJoinImageAssetSeed(
  workspaceId: string,
  projectId: string,
): JoinImageAssetSeed | null {
  const seed = readRaw()
  if (!seed) return null
  if (seed.workspaceId !== workspaceId || seed.projectId !== projectId) return null
  clearJoinImageAssetSeed()
  return seed
}

export function clearJoinImageAssetSeed(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(JOIN_IMAGE_ASSET_SEED_KEY)
  } catch {
    /* ignore */
  }
}

export function makeJoinImageAssetSeedNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `join-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}
